import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Coins, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import api from '../lib/api';

/**
 * AddPromoCreditsModal - Admin modal for awarding promotional Reader Credits
 * 
 * Features:
 * - Displays date/time (MST), user info, and admin info
 * - Credit amount selection (5-100 in increments of 5)
 * - Reason selection with predefined categories
 * - Optional notes field
 * - Immediate processing and database update
 */

const PROMO_REASONS = [
  { value: 'Promotional', label: 'Promotional - Marketing/campaigns' },
  { value: 'Refunded', label: 'Refunded - Service issue compensation' },
  { value: 'Testing', label: 'Testing - Testing purposes' },
  { value: 'User Complaint', label: 'User Complaint - Complaint resolution' },
  { value: 'Good Faith', label: 'Good Faith - Goodwill gesture' },
  { value: 'Other', label: 'Other - Other administrative reason' },
];

// Generate credit amount options (5 to 100 in increments of 5)
const CREDIT_AMOUNTS = Array.from({ length: 20 }, (_, i) => (i + 1) * 5);

const AddPromoCreditsModal = ({ 
  isOpen, 
  onClose, 
  user,
  adminUser,
  onSuccess 
}) => {
  const [creditAmount, setCreditAmount] = useState(5);
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [currentDateTime, setCurrentDateTime] = useState('');

  // Update date/time every second
  useEffect(() => {
    const updateDateTime = () => {
      const now = new Date();
      // Convert to MST (UTC-7)
      const mstOffset = -7 * 60; // MST is UTC-7
      const localOffset = now.getTimezoneOffset();
      const mstTime = new Date(now.getTime() + (localOffset + mstOffset) * 60000);
      
      const formatted = mstTime.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      }) + ' MST';
      setCurrentDateTime(formatted);
    };

    updateDateTime();
    const interval = setInterval(updateDateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setCreditAmount(5);
      setReason('');
      setNotes('');
      setError(null);
      setSuccess(null);
    }
  }, [isOpen]);

  const getUserFullName = () => {
    if (user?.first_name || user?.last_name) {
      return `${user.first_name || ''} ${user.last_name || ''}`.trim();
    }
    return user?.name || user?.email || 'Unknown User';
  };

  const getAdminName = () => {
    if (adminUser?.first_name || adminUser?.last_name) {
      return `${adminUser.first_name || ''} ${adminUser.last_name || ''}`.trim();
    }
    return adminUser?.name || adminUser?.email || 'Admin';
  };

  const handleSubmit = async () => {
    if (!reason) {
      setError('Please select a reason for the credit award');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.post(`/admin/users/${user.id}/promo-credits`, {
        credit_amount: creditAmount,
        promo_reason: reason,
        notes: notes.trim() || null
      });

      setSuccess({
        message: response.data.message,
        newBalance: response.data.new_balance,
        creditsAwarded: response.data.credits_awarded
      });

      // Call onSuccess callback to refresh the activity data
      if (onSuccess) {
        onSuccess(response.data);
      }

      // Auto-close after 2 seconds on success
      setTimeout(() => {
        onClose();
      }, 2000);

    } catch (err) {
      console.error('Error awarding promo credits:', err);
      setError(err.response?.data?.detail || 'Failed to award promotional credits');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleCancel}>
      <DialogContent className="sm:max-w-lg bg-slate-900 border-slate-700 text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-bold text-white">
            <Coins className="w-6 h-6 text-amber-400" />
            Add Promotional Credits
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Award promotional Reader Credits to this user's account
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="py-6 text-center">
            <CheckCircle2 className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <p className="text-lg font-semibold text-white mb-2">{success.message}</p>
            <p className="text-slate-400">
              New Balance: <span className="text-green-400 font-bold">{success.newBalance.toFixed(2)} RC</span>
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-4 py-4">
              {/* Date/Time Display */}
              <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Transaction Date/Time</p>
                <p className="text-sm font-mono text-slate-200">{currentDateTime}</p>
              </div>

              {/* User Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                  <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">User ID</p>
                  <p className="text-xs font-mono text-slate-300 truncate" title={user?.id}>
                    {user?.id}
                  </p>
                </div>
                <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                  <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">User Full Name</p>
                  <p className="text-sm text-slate-200 truncate" title={getUserFullName()}>
                    {getUserFullName()}
                  </p>
                </div>
              </div>

              {/* Admin Info */}
              <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Awarded By (Admin)</p>
                <p className="text-sm text-slate-200">{getAdminName()}</p>
              </div>

              {/* Credit Amount Selection */}
              <div>
                <label className="text-sm font-medium text-slate-300 mb-2 block">
                  Credits to Award <span className="text-red-400">*</span>
                </label>
                <Select value={String(creditAmount)} onValueChange={(v) => setCreditAmount(Number(v))}>
                  <SelectTrigger className="bg-slate-800 border-slate-600 text-white">
                    <SelectValue placeholder="Select amount" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-600">
                    {CREDIT_AMOUNTS.map((amount) => (
                      <SelectItem 
                        key={amount} 
                        value={String(amount)}
                        className="text-white hover:bg-slate-700"
                      >
                        {amount} RC
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">Select between 5-100 credits (increments of 5)</p>
              </div>

              {/* Reason Selection */}
              <div>
                <label className="text-sm font-medium text-slate-300 mb-2 block">
                  Reason for Award <span className="text-red-400">*</span>
                </label>
                <Select value={reason} onValueChange={setReason}>
                  <SelectTrigger className="bg-slate-800 border-slate-600 text-white">
                    <SelectValue placeholder="Select a reason" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-600">
                    {PROMO_REASONS.map((r) => (
                      <SelectItem 
                        key={r.value} 
                        value={r.value}
                        className="text-white hover:bg-slate-700"
                      >
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Optional Notes */}
              <div>
                <label className="text-sm font-medium text-slate-300 mb-2 block">
                  Notes <span className="text-slate-500">(optional)</span>
                </label>
                <Input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Additional notes for this transaction..."
                  className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
                  maxLength={500}
                />
              </div>

              {/* Error Message */}
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-900/30 border border-red-500/50 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                  <p className="text-sm text-red-300">{error}</p>
                </div>
              )}
            </div>

            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={loading}
                className="border-slate-600 text-slate-300 hover:bg-slate-800"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={loading || !reason}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Coins className="w-4 h-4 mr-2" />
                    Award {creditAmount} Credits
                  </>
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AddPromoCreditsModal;
